// src/pages/agenda.js
import React, { useEffect, useMemo, useState } from "react";

// Base path degli endpoint PHP
const API = "/preventivi/agenda/api";

// utility fetch JSON (GET/POST)
async function apiGet(url) {
  const r = await fetch(url, { credentials: "same-origin" });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || `HTTP ${r.status}`);
  return data;
}
async function apiPost(path, body) {
  const r = await fetch(`${API}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || `HTTP ${r.status}`);
  return data;
}

// date helpers
function toLocalDateTime(value) {
  // accetta "YYYY-MM-DD HH:mm:ss" o ISO; torna "YYYY-MM-DDTHH:mm"
  if (!value) return "";
  const s = value.replace(" ", "T").slice(0, 16);
  return s;
}
function fromDateInput(d, t = "00:00") {
  // d="YYYY-MM-DD", t="HH:mm" -> "YYYY-MM-DD HH:mm:00"
  if (!d) return null;
  return `${d} ${t}:00`;
}
function weekRangeAroundToday() {
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - 3);
  const end = new Date(now);
  end.setDate(now.getDate() + 4);
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = (dt) =>
    `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  return { from: fmt(start), to: fmt(end) };
}

export default function Agenda() {
  // filtro intervallo
  const initial = useMemo(() => weekRangeAroundToday(), []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);

  // stato lista
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");

  // form "nuovo appuntamento" (campi minimi necessari)
  const [newForm, setNewForm] = useState({
    cliente_id: 1,
    created_by_operator_id: 1,
    zona_id: 1,
    start_date: "", // "YYYY-MM-DD"
    start_time: "09:00",
    end_date: "",
    end_time: "10:00",
    fascia: "E",
    nome_cliente: "",
    cliente_telefono: "",
    cliente_indirizzo: "",
    note: "",
  });

  // carica eventi
  async function load() {
    try {
      setLoading(true);
      setError("");
      const data = await apiGet(
        `${API}/list_events.php?from=${encodeURIComponent(
          from
        )}&to=${encodeURIComponent(to)}`
      );
      setEvents(data.events || []);
    } catch (e) {
      console.error(e);
      setError(e.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // azioni riga
  async function confirm(id) {
    await apiPost("confirm_event.php", {
      id,
      confirmed_by_operator_id: 1,
      stato: "confermato",
    });
    await load();
  }
  async function cancel(id) {
    const motivo = prompt("Motivo annullamento?", "Cliente impossibilitato");
    if (motivo === null) return;
    await apiPost("cancel_event.php", {
      id,
      motivo: motivo || "Annullato",
      cancelled_by_operator_id: 1,
      stato: "annullato",
    });
    await load();
  }
  async function remove(id) {
    if (!window.confirm("Eliminare definitivamente l'appuntamento?")) return;
    await apiPost("delete_event.php", { id });
    await load();
  }
  async function quickMove(ev) {
    // semplice spostamento +30 minuti
    const start = new Date(ev.start_dt.replace(" ", "T"));
    const end = new Date(ev.end_dt.replace(" ", "T"));
    start.setMinutes(start.getMinutes() + 30);
    end.setMinutes(end.getMinutes() + 30);
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
        d.getHours()
      )}:${pad(d.getMinutes())}:00`;
    await apiPost("move_event.php", {
      id: ev.id,
      start_dt: fmt(start),
      end_dt: fmt(end),
      fascia: ev.fascia || "E",
    });
    await load();
  }

  // crea nuovo
  async function createNew(e) {
    e.preventDefault();
    const start_dt = fromDateInput(newForm.start_date, newForm.start_time);
    const end_dt = fromDateInput(
      newForm.end_date || newForm.start_date,
      newForm.end_time
    );
    if (!start_dt || !end_dt) {
      alert("Inserisci data/ora inizio e fine");
      return;
    }
    await apiPost("save_event.php", {
      cliente_id: Number(newForm.cliente_id),
      created_by_operator_id: Number(newForm.created_by_operator_id),
      zona_id: newForm.zona_id ? Number(newForm.zona_id) : null,
      start_dt,
      end_dt,
      fascia: newForm.fascia || "E",
      nome_cliente: newForm.nome_cliente || "Cliente",
      cliente_telefono: newForm.cliente_telefono || "0000000000",
      cliente_indirizzo: newForm.cliente_indirizzo || "-",
      note: newForm.note || "",
    });
    // reset rapido e ricarica
    setNewForm((f) => ({ ...f, start_date: "", end_date: "", note: "" }));
    await load();
  }

  // preset veloci
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = (dt) =>
    `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;

  return (
    <div style={{ padding: 16 }}>
      <h2>Agenda (lista rapida)</h2>

      {/* filtro */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "end",
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <label>Dal</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div>
          <label>Al</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>

        <button onClick={load} disabled={loading}>
          {loading ? "Carico…" : "Ricarica"}
        </button>

        {/* preset veloci */}
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            onClick={() => {
              const d = new Date();
              const today = fmt(d);
              setFrom(today);
              setTo(today);
              load();
            }}
          >
            Oggi
          </button>
          <button
            type="button"
            onClick={() => {
              const d = new Date();
              const fromD = new Date();
              fromD.setDate(d.getDate() - 3);
              const toD = new Date();
              toD.setDate(d.getDate() + 3);
              setFrom(fmt(fromD));
              setTo(fmt(toD));
              load();
            }}
          >
            7 giorni
          </button>
          <button
            type="button"
            onClick={() => {
              const d = new Date();
              const toD = new Date();
              toD.setDate(d.getDate() + 30);
              setFrom(fmt(d));
              setTo(fmt(toD));
              load();
            }}
          >
            30 giorni
          </button>
        </div>

        {error && <span style={{ color: "crimson" }}>{error}</span>}
      </div>

      {/* nuovo */}
      <details style={{ marginBottom: 16 }}>
        <summary>
          <strong>Nuovo appuntamento</strong>
        </summary>
        <form
          onSubmit={createNew}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 12,
            marginTop: 12,
          }}
        >
          <div>
            <label>Cliente ID</label>
            <input
              type="number"
              value={newForm.cliente_id}
              onChange={(e) =>
                setNewForm({ ...newForm, cliente_id: e.target.value })
              }
            />
          </div>
          <div>
            <label>Operatore (creator)</label>
            <input
              type="number"
              value={newForm.created_by_operator_id}
              onChange={(e) =>
                setNewForm({
                  ...newForm,
                  created_by_operator_id: e.target.value,
                })
              }
            />
          </div>
          <div>
            <label>Zona</label>
            <input
              type="number"
              value={newForm.zona_id}
              onChange={(e) =>
                setNewForm({ ...newForm, zona_id: e.target.value })
              }
            />
          </div>
          <div>
            <label>Fascia</label>
            <input
              value={newForm.fascia}
              onChange={(e) =>
                setNewForm({ ...newForm, fascia: e.target.value })
              }
            />
          </div>

          <div>
            <label>Data</label>
            <input
              type="date"
              value={newForm.start_date}
              onChange={(e) =>
                setNewForm({
                  ...newForm,
                  start_date: e.target.value,
                  end_date: e.target.value,
                })
              }
            />
          </div>
          <div>
            <label>Ora inizio</label>
            <input
              type="time"
              value={newForm.start_time}
              onChange={(e) =>
                setNewForm({ ...newForm, start_time: e.target.value })
              }
            />
          </div>
          <div>
            <label>Ora fine</label>
            <input
              type="time"
              value={newForm.end_time}
              onChange={(e) =>
                setNewForm({ ...newForm, end_time: e.target.value })
              }
            />
          </div>
          <div>
            <label>Telefono</label>
            <input
              value={newForm.cliente_telefono}
              onChange={(e) =>
                setNewForm({
                  ...newForm,
                  cliente_telefono: e.target.value,
                })
              }
            />
          </div>

          <div>
            <label>Nome</label>
            <input
              value={newForm.nome_cliente}
              onChange={(e) =>
                setNewForm({ ...newForm, nome_cliente: e.target.value })
              }
            />
          </div>
          <div>
            <label>Indirizzo</label>
            <input
              value={newForm.cliente_indirizzo}
              onChange={(e) =>
                setNewForm({
                  ...newForm,
                  cliente_indirizzo: e.target.value,
                })
              }
            />
          </div>
          <div style={{ gridColumn: "1 / span 3" }}>
            <label>Note</label>
            <input
              value={newForm.note}
              onChange={(e) =>
                setNewForm({ ...newForm, note: e.target.value })
              }
            />
          </div>

          <div style={{ gridColumn: "1 / span 4", textAlign: "right" }}>
            <button type="submit">Crea</button>
          </div>
        </form>
      </details>

      {/* tabella */}
      <div style={{ overflowX: "auto" }}>
        <table
          className="table-agenda"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              <th>ID</th>
              <th>Inizio</th>
              <th>Fine</th>
              <th>Cliente</th>
              <th>Telefono</th>
              <th>Indirizzo</th>
              <th>Stato</th>
              <th>Fascia</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr key={ev.id}>
                <td>{ev.id}</td>
                <td>
                  <input
                    type="datetime-local"
                    defaultValue={toLocalDateTime(ev.start_dt)}
                    readOnly
                  />
                </td>
                <td>
                  <input
                    type="datetime-local"
                    defaultValue={toLocalDateTime(ev.end_dt)}
                    readOnly
                  />
                </td>
                <td>{ev.nome_cliente}</td>

                <td>
                  {ev.cliente_telefono ? (
                    <>
                      <a href={`tel:${ev.cliente_telefono}`} title="Chiama">
                        {ev.cliente_telefono}
                      </a>{" "}
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() =>
                          navigator.clipboard.writeText(ev.cliente_telefono)
                        }
                        title="Copia"
                      >
                        Copia
                      </button>
                    </>
                  ) : (
                    "-"
                  )}
                </td>

                <td>{ev.cliente_indirizzo}</td>

                <td>
                  <span
                    className={`pill pill-${(ev.stato || "vuoto").replace(
                      /\W/g,
                      "_"
                    )}`}
                  >
                    {ev.stato || "-"}
                  </span>
                </td>
                <td>{ev.fascia || "-"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button onClick={() => confirm(ev.id)}>Conferma</button>{" "}
                  <button onClick={() => cancel(ev.id)}>Annulla</button>{" "}
                  <button onClick={() => quickMove(ev)}>+30m</button>{" "}
                  <button
                    onClick={() => remove(ev.id)}
                    style={{ color: "crimson" }}
                  >
                    Elimina
                  </button>
                </td>
              </tr>
            ))}
            {!events.length && (
              <tr>
                <td
                  colSpan={9}
                  style={{ textAlign: "center", opacity: 0.7, padding: 12 }}
                >
                  Nessun appuntamento nel periodo selezionato.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .table-agenda th, .table-agenda td { border: 1px solid #ddd; padding: 6px 8px; }
        .table-agenda th { background: #f5f5f5; text-align: left; }
        details > summary { cursor: pointer; }
        label { display:block; font-size: 12px; opacity: .7; margin-bottom: 4px; }
        input { width: 100%; }
        button { padding: 6px 10px; }
        .page-container { max-width: 1100px; margin: 0 auto; }

        /* header sticky + zebra */
        .table-agenda thead th{ position: sticky; top: 0; background:#fafafa; z-index:1; }
        .table-agenda tbody tr:nth-child(odd){ background:#fcfcfc; }

        /* bottoncino ghost (copia tel) */
        .btn-ghost{
          border: 1px solid #ddd; background: #f8f8f8; padding: 3px 8px;
          border-radius: 6px; font-size: 12px; cursor: pointer;
        }
        .btn-ghost:hover{ background:#f0f0f0; }

        /* pill per stato */
        .pill{
          display:inline-block; padding: 4px 8px; border-radius: 999px;
          font-size: 12px; font-weight: 700; line-height: 1; white-space: nowrap;
          background:#eee; color:#333; border:1px solid #e0e0e0;
        }
        .pill-prenotato{ background:#eef; color:#264; border-color:#dde; }
        .pill-confermato{ background:#e6f7ed; color:#136d3a; border-color:#cdebd9; }
        .pill-assegnato{ background:#eaf3ff; color:#0d4a8c; border-color:#d6e7ff; }
        .pill-rilievo_fatto{ background:#fff6e6; color:#8a5a00; border-color:#ffe7bf; }
        .pill-senior_da_programmare{ background:#fff2d6; color:#7a4d00; border-color:#ffe5ad; }
        .pill-chiuso{ background:#eee; color:#444; border-color:#ddd; }
        .pill-annullato, .pill-no_show{ background:#fde8e8; color:#8a1a1a; border-color:#f7caca; }
      `}</style>
    </div>
  );
}
